import React from "react";
import { MapPin, Check } from "lucide-react";
import { HeroCTAs } from "./HeroCTAs";
import { Reveal } from "./Reveal";

const cities = ["Saint-Denis", "Saint-Paul", "Saint-Pierre", "Le Tampon", "Saint-André", "Saint-Louis", "Le Port", "Saint-Benoît"];

const specialties = [
  {
    title: "Retour Affectif 974",
    text: [
      "Votre partenaire est parti, il ne répond plus ou s'éloigne de vous ? Ne restez pas seul(e) face à la douleur d'une séparation.",
      "Maître Diamma analyse votre situation amoureuse et réalise un travail personnalisé pour raviver les sentiments, apaiser les conflits et faire revenir l'être aimé à vos côtés, partout à La Réunion.",
    ],
    points: ["Retour de l'être aimé rapide", "Fin des disputes et de la jalousie", "Fidélité et amour durable dans le couple"],
  },
  {
    title: "Marabout Africain",
    text: [
      "Héritier d'un savoir ancestral transmis de génération en génération, Maître Diamma pratique les rituels traditionnels africains avec sérieux et respect.",
      "Chaque travail est adapté à votre problème : amour, chance, travail, argent, réussite ou protection. Vous êtes écouté(e), conseillé(e) et accompagné(e) à chaque étape.",
    ],
    points: ["Rituels puissants et personnalisés", "Conseils honnêtes et sans jugement", "Discrétion totale garantie"],
  },
  {
    title: "Désenvoûtement Réunion",
    text: [
      "Malchance qui dure, fatigue inexpliquée, blocages en amour ou au travail, mauvais rêves… Ces signes peuvent révéler un sort ou des énergies négatives.",
      "Maître Diamma identifie l'origine du mal, vous libère des envoûtements et vous protège durablement pour que vous retrouviez paix, énergie et sérénité.",
    ],
    points: ["Levée des sorts et du mauvais œil", "Nettoyage des énergies négatives", "Protection de vous et de votre famille"],
  },
];

const SpecialtyCard = ({ sp, i }) => (
  <Reveal delay={i * 120}>
    <div className="lux-card h-full p-7" data-testid={`hero-specialty-${i}`}>
      <h3 className="font-heading text-xl text-amber-200 mb-4">{sp.title}</h3>
      {sp.text.map((t) => (
        <p key={t} className="text-zinc-300 text-sm leading-relaxed mb-3">{t}</p>
      ))}
      <ul className="mt-4 space-y-2">
        {sp.points.map((pt) => (
          <li key={pt} className="flex items-start text-sm text-zinc-200">
            <Check className="w-4 h-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />{pt}
          </li>
        ))}
      </ul>
    </div>
  </Reveal>
);

const Hero = () => (
  <section id="accueil" className="relative overflow-hidden lux-section-glow">
    <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-10 pb-20 md:pb-28 text-center">
      <div className="fade-up">
        <span className="inline-block mb-4 px-5 py-2 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-300 text-xs sm:text-sm tracking-[0.25em] uppercase">
          N°1 à La Réunion · 974
        </span>
        <h1 className="font-heading text-[1.65rem] min-[400px]:text-3xl sm:text-6xl lg:text-7xl font-semibold mb-3 leading-tight whitespace-nowrap" data-testid="hero-h1">
          <span className="text-white">Marabout </span><span className="gold-text">Réunion</span>
        </h1>
        <p className="font-elegant italic text-xl sm:text-2xl md:text-3xl text-amber-100/90 mb-5" data-testid="hero-subtitle">
          Maître Diamma — Voyant Médium 974
        </p>
        <div className="gold-divider w-40 mx-auto mb-6" />
        <p className="text-base md:text-lg text-zinc-300 leading-relaxed max-w-3xl mx-auto mb-10" data-testid="hero-intro">
          Votre cœur souffre, votre couple traverse une crise ou l'être aimé s'est éloigné ?
          <strong className="text-amber-300"> Maître Diamma</strong>, puissant marabout africain, met son don et des années d'expérience à votre service pour un
          <strong className="text-amber-300"> retour affectif rapide et durable</strong> à La Réunion.
          Écoute sincère, travail sérieux et discrétion totale, à Saint-Denis, Saint-Pierre, Le Tampon, Saint-Paul et dans toute l'île.
          <span className="text-white font-medium"> N'attendez plus : votre bonheur peut recommencer dès aujourd'hui.</span>
        </p>
      </div>

      <div className="fade-up" style={{ animationDelay: "200ms" }}>
        <HeroCTAs />
        <p className="mt-6 text-sm text-zinc-400 tracking-wide">Consultation discrète 7j/7 · 7h00 – 23h30 (heure de La Réunion)</p>
      </div>

      <div className="mt-10 fade-up" style={{ animationDelay: "350ms" }}>
        <div className="flex items-center justify-center mb-4 text-zinc-400 text-sm">
          <MapPin className="w-4 h-4 text-amber-400 mr-2" /> Intervention sur toute La Réunion
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {cities.map((c) => (
            <span key={c} className="px-3 py-1 rounded-full text-xs sm:text-sm border border-amber-500/20 text-amber-200/90 bg-zinc-900/60">{c}</span>
          ))}
        </div>
      </div>

      <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
        {specialties.map((sp, i) => (
          <SpecialtyCard key={sp.title} sp={sp} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Hero;
